/**
 * Form component for validating and customizing normalized tracker parameters.
 *
 * This component displays the pre-configured parameters for each selected source
 * and allows users to customize optional parameters before creating trackers.
 *
 * The main search value is already set in the wizard (step 2) and displayed here
 * for reference. Source-specific parameters are pre-filled based on the API response.
 */

import React, { useEffect, useMemo, useImperativeHandle, forwardRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { FaChevronDown } from "react-icons/fa";
import { HiOutlineLink } from "react-icons/hi2";

import {
  Alert,
  Autocomplete,
  Button,
  Card,
  CardBody,
  Input,
  Switch,
  Typography,
} from "@/components/ui";
import { SourceIcon } from "@/components/resource-card";
import useToggle from "@/hooks/use-toggle";
import useTranslation from "@/hooks/use-translation";
import type {
  NormalizedTrackerInfo,
  ParameterSchema,
  TrackerTypeName,
  TrackerTypeResponse,
} from "@/models/normalized-tracker";
import { TRACKER_TYPE_LABELS } from "@/models/normalized-tracker";

/**
 * Form values structure:
 * - sources: source -> param_name -> value
 */
export interface NormalizedTrackerFormValues {
  /** Source-specific parameters */
  sources: Record<string, Record<string, unknown>>;
}

/**
 * Ref handle for external form control.
 */
export interface NormalizedTrackerFormHandle {
  /** Trigger form submission programmatically */
  submit: () => void;
}

interface NormalizedTrackerFormProps {
  /** The tracker type being configured */
  trackerType: TrackerTypeName;
  /** List of selected source names */
  selectedSources: string[];
  /** The search value entered in step 2 */
  searchValue: string;
  /** Tracker info from API (includes configured_params) */
  trackerInfo: TrackerTypeResponse;
  /** Callback when form is submitted */
  onSubmit: (values: NormalizedTrackerFormValues) => void;
  /** Callback to go back */
  onBack?: () => void;
  /** Whether the form is submitting */
  isSubmitting?: boolean;
  /** Submit button text */
  submitLabel?: string;
  /** Hide the form's own buttons (when parent manages navigation) */
  hideButtons?: boolean;
}

/**
 * Render an input for a single parameter.
 */
const ParameterInput: React.FC<{
  param: ParameterSchema;
  control: ReturnType<typeof useForm>["control"];
  fieldPrefix: string;
  defaultValue?: unknown;
}> = ({ param, control, fieldPrefix, defaultValue }) => {
  const fieldName = `${fieldPrefix}.${param.name}`;

  // String or integer input
  if (param.type === "string" || param.type === "integer") {
    return (
      <Controller
        name={fieldName}
        control={control}
        defaultValue={defaultValue ?? param.default ?? ""}
        rules={{
          required: param.required ? "Ce champ est requis" : false,
        }}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            value={field.value ?? ""}
            label={param.title || param.name}
            required={param.required}
            type={param.type === "integer" ? "number" : "text"}
            placeholder={param.example || ""}
            helperText={param.description}
            error={error?.message}
            fullWidth
          />
        )}
      />
    );
  }

  // Boolean switch
  if (param.type === "boolean") {
    return (
      <Controller
        name={fieldName}
        control={control}
        defaultValue={defaultValue ?? param.default ?? false}
        render={({ field }) => (
          <div className="flex flex-col gap-1">
            <Switch
              checked={field.value ?? false}
              onChange={(checked) => field.onChange(checked)}
              label={param.title || param.name}
            />
            {param.description && (
              <Typography variant="small" color="tertiary">
                {param.description}
              </Typography>
            )}
          </div>
        )}
      />
    );
  }

  // Array input (tags-like)
  if (param.type === "array") {
    return (
      <Controller
        name={fieldName}
        control={control}
        defaultValue={defaultValue ?? param.default ?? []}
        rules={{
          required: param.required ? "Ce champ est requis" : false,
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Autocomplete
            multiple
            freeSolo
            options={param.enum?.map((v) => ({ label: v, value: v })) || []}
            value={(value || []).map((v: string) => ({ label: v, value: v }))}
            onChange={(newValue) => {
              const values = (newValue as { label: string; value: string }[]).map(
                (item) => (typeof item === "string" ? item : item.value)
              );
              onChange(values);
            }}
            label={param.title || param.name}
            placeholder={param.example || "Tapez et appuyez sur Entrée..."}
            error={error?.message}
          />
        )}
      />
    );
  }

  // Fallback for unknown types
  return (
    <Controller
      name={fieldName}
      control={control}
      defaultValue={defaultValue ?? param.default ?? ""}
      render={({ field }) => (
        <Input
          {...field}
          value={field.value ?? ""}
          label={param.title || param.name}
          placeholder={param.example || ""}
          fullWidth
        />
      )}
    />
  );
};

/**
 * Card for a single source's parameters.
 */
const SourceParametersCard: React.FC<{
  source: string;
  info: NormalizedTrackerInfo;
  control: ReturnType<typeof useForm>["control"];
  fieldPrefix: string;
  configuredParams?: Record<string, unknown>;
  /** The specific search term for this tracker (after boolean decomposition) */
  searchTerm?: string;
}> = ({ source, info, control, fieldPrefix, configuredParams, searchTerm }) => {
  const [showOptional, toggleShowOptional] = useToggle();

  // Get the names of parameters that are already configured (from search value)
  const configuredParamNames = useMemo(
    () => new Set(Object.keys(configuredParams || {})),
    [configuredParams]
  );

  // Filter out parameters that are already configured by the search value
  const remainingParams = useMemo(
    () => info.parameters.filter((p) => !configuredParamNames.has(p.name)),
    [info.parameters, configuredParamNames]
  );

  const requiredParams = useMemo(
    () => remainingParams.filter((p) => p.required),
    [remainingParams]
  );

  const optionalParams = useMemo(
    () => remainingParams.filter((p) => !p.required),
    [remainingParams]
  );

  // Check if the search value parameter is shown (for display purposes)
  const configuredParamDisplay = useMemo(() => {
    if (!configuredParams || Object.keys(configuredParams).length === 0) {
      return null;
    }
    return Object.entries(configuredParams).map(([key, value]) => ({
      name: key,
      value: String(value),
    }));
  }, [configuredParams]);

  return (
    <Card variant="outlined" className="mb-4">
      <CardBody className="space-y-4">
        {/* Source header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SourceIcon source={source} size="md" />
            <div>
              <Typography variant="h6" className="font-semibold capitalize">
                {source}{searchTerm ? ` — "${searchTerm}"` : ""}
              </Typography>
              <Typography variant="small" color="tertiary">
                {info.endpoint}
              </Typography>
            </div>
          </div>
          {info.has_chain && (
            <div className="flex items-center gap-1 text-accent">
              <HiOutlineLink className="w-4 h-4" />
              <Typography variant="small">Tracker chaîné</Typography>
            </div>
          )}
        </div>

        {/* Chain info alert */}
        {info.has_chain && info.chain_endpoint && (
          <Alert severity="info" className="text-sm">
            Ce tracker effectue d'abord une recherche, puis récupère les contenus
            de chaque résultat via <code>{info.chain_endpoint}</code>
          </Alert>
        )}

        {/* Configured parameters (read-only display) */}
        {configuredParamDisplay && configuredParamDisplay.length > 0 && (
          <div className="bg-accent-muted/30 border border-accent/30 rounded-lg p-3">
            <Typography variant="small" color="secondary" className="mb-2">
              Paramètre de recherche (pré-configuré) :
            </Typography>
            {configuredParamDisplay.map(({ name, value }) => (
              <div key={name} className="flex items-center gap-2">
                <Typography variant="body2" className="font-medium capitalize">
                  {name}:
                </Typography>
                <Typography variant="body2" className="text-accent font-semibold">
                  {value}
                </Typography>
              </div>
            ))}
          </div>
        )}

        {/* Required parameters */}
        {requiredParams.length > 0 && (
          <div className="space-y-3">
            <Typography variant="small" color="secondary">
              Paramètres requis :
            </Typography>
            {requiredParams.map((param) => (
              <ParameterInput
                key={param.name}
                param={param}
                control={control}
                fieldPrefix={fieldPrefix}
                defaultValue={configuredParams?.[param.name]}
              />
            ))}
          </div>
        )}

        {/* Optional parameters (collapsible) */}
        {optionalParams.length > 0 && (
          <div>
            <button
              type="button"
              className={`w-full flex cursor-pointer items-center justify-between rounded-arc-md border px-4 py-3 transition-all duration-arc-fast text-sm font-semibold
                ${
                  showOptional
                    ? "bg-accent-subtle border-accent text-accent-dark"
                    : "bg-surface-elevated border-arc-border text-content-primary hover:border-arc-border-strong hover:bg-surface-secondary"
                }`}
              onClick={toggleShowOptional}
            >
              <span>Paramètres optionnels ({optionalParams.length})</span>
              <FaChevronDown
                className={`transition-transform duration-arc-normal ${
                  showOptional ? "rotate-180" : ""
                }`}
              />
            </button>
            {showOptional && (
              <div className="mt-2 space-y-3 rounded-arc-md border border-arc-border bg-surface-secondary p-4">
                {optionalParams.map((param) => (
                  <ParameterInput
                    key={param.name}
                    param={param}
                    control={control}
                    fieldPrefix={fieldPrefix}
                    defaultValue={configuredParams?.[param.name]}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* No parameters message */}
        {remainingParams.length === 0 && !configuredParamDisplay && (
          <Typography variant="body2" color="tertiary">
            Aucun paramètre configurable pour cette source.
          </Typography>
        )}

        {remainingParams.length === 0 && configuredParamDisplay && (
          <Typography variant="body2" color="tertiary">
            Aucun paramètre supplémentaire à configurer.
          </Typography>
        )}
      </CardBody>
    </Card>
  );
};

/**
 * Main form component for normalized tracker validation.
 */
const NormalizedTrackerForm = forwardRef<NormalizedTrackerFormHandle, NormalizedTrackerFormProps>(
  (
    {
      trackerType,
      selectedSources,
      searchValue,
      trackerInfo,
      onSubmit,
      onBack,
      isSubmitting = false,
      submitLabel = "Créer les trackers",
      hideButtons = false,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { control, handleSubmit, reset } = useForm<NormalizedTrackerFormValues>({
      defaultValues: { sources: {} },
    });

    // Reset form when trackerInfo changes
    useEffect(() => {
      reset({ sources: {} });
    }, [trackerInfo, reset]);

    const onFormSubmit = (data: NormalizedTrackerFormValues) => {
      // Filter out empty values from sources
      const cleanedSources: Record<string, Record<string, unknown>> = {};
      for (const [source, params] of Object.entries(data.sources || {})) {
        cleanedSources[source] = {};
        for (const [key, value] of Object.entries(params || {})) {
          if (value !== "" && value !== undefined && value !== null) {
            if (Array.isArray(value) && value.length === 0) continue;
            cleanedSources[source][key] = value;
          }
        }
      }
      onSubmit({ sources: cleanedSources });
    };

    // Expose submit method via ref
    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(onFormSubmit)();
      },
    }));

    if (!trackerInfo || trackerInfo.sources.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <Typography variant="body1" color="secondary">
            Aucune source disponible pour ce type de tracker.
          </Typography>
          {onBack && !hideButtons && (
            <Button variant="outline" onClick={onBack} className="mt-4">
              Retour
            </Button>
          )}
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
        {/* Header */}
        <div className="mb-4">
          <Typography variant="h6" className="text-content-primary">
            Validation des trackers
          </Typography>
          <Typography variant="body2" color="secondary">
            {TRACKER_TYPE_LABELS[trackerType]} : <strong>{searchValue}</strong> •{" "}
            {t("n_sources", { count: selectedSources.length })}
          </Typography>
        </div>

        {/* Info banner */}
        <Alert severity="info" className="mb-4">
          {t("n_trackers_count", { count: trackerInfo.sources.filter((e) => selectedSources.includes(e.source)).length })}{" "}
          seront créés à partir de votre recherche "{searchValue}".
        </Alert>

        {/* Tracker cards - one per source/term combination */}
        {trackerInfo.sources
          .filter((entry) => selectedSources.includes(entry.source))
          .map((entry, index) => (
            <SourceParametersCard
              key={`${entry.source}-${entry.search_term}-${index}`}
              source={entry.source}
              info={entry.info}
              control={control as any}
              fieldPrefix={`sources.${entry.source}`}
              configuredParams={entry.info.configured_params}
              searchTerm={entry.search_term}
            />
          ))}

        {/* Submit button - only show when not hiding buttons */}
        {!hideButtons && (
          <div className="flex justify-end gap-3 pt-4 border-t border-arc-border">
            {onBack && (
              <Button variant="outline" onClick={onBack} type="button">
                Retour
              </Button>
            )}
            <Button
              type="submit"
              variant="accent"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {submitLabel}
            </Button>
          </div>
        )}
      </form>
    );
  }
);

NormalizedTrackerForm.displayName = "NormalizedTrackerForm";

export default NormalizedTrackerForm;
